import { Card, Checkbox, Col, Divider, Form, Input, message, Modal, Row, Spin, Tag } from 'antd';
import React from 'react';
import { ITableRef } from '@/components/ZTable';
import { useTranslation } from 'react-i18next';
import { formatApiDateTime, formatDisplayDate, ModalWidth, ROW_GUTTER, VALIDATE_REQUIRED } from '@/config';
import { SaveOutlined } from '@ant-design/icons';
import SeatingModelInput from '@/components/FormItems/SeatingModelInput';
import RouteInput from '@/components/FormItems/RouteInput';
import TripGroupInput from '@/components/FormItems/TripGroupInput';
import './styles.less';
import AppDateTimePicker from '@/components/FormItems/DateTimePicker';
import { DayOfWeekType } from '@/constants';
import { range } from 'lodash';
import moment from 'moment';
import { RouteStopDto, RouteBasicInfoModel, TripStopModel } from '@/__generated';
import { apiSdk } from '@/httpClient';
type Props = {
  onClose: (reload: boolean) => void;
};

interface SelectableRouteStop extends RouteStopDto {
  selected: boolean;
}
export default function CreateTripModal({ onClose }: Props) {
  const { t } = useTranslation();
  const zTb = React.useRef<ITableRef>();
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);
  const [routeDetail, setRouteDetail] = React.useState<RouteBasicInfoModel>();
  const [stops, setStops] = React.useState<SelectableRouteStop[]>([]);

  const routeId = Form.useWatch('routeId', form);
  React.useEffect(() => {
    if (routeId) {
      const apis = [apiSdk.RouteService.getBasicInfo({ id: routeId }), apiSdk.RouteService.getRouteStops({ routeId: routeId })];
      setLoading(true);
      Promise.all(apis)
        .then(([routeDetail, stopResponse]) => {
          setRouteDetail(routeDetail as any);
          setStops((stopResponse as RouteStopDto[]).map(x => ({ ...x, selected: true })));
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setRouteDetail(undefined);
      setStops([]);
    }
  }, [routeId]);

  const isRecurred = Form.useWatch('isRecurred', form);
  const plannedDepartureDateTime = Form.useWatch('plannedDepartureDateTime', form);
  const recurrenceNumDays: number[] = Form.useWatch('recurrenceNumDays', form);
  const recurrenceDates = React.useMemo(() => {
    if (isRecurred && plannedDepartureDateTime && recurrenceNumDays?.length) {
      const dates: string[] = [];
      range(1, 31).forEach(i => {
        let date = moment().add(i, 'day');
        let numOfWeek = date.day();
        if (recurrenceNumDays.includes(numOfWeek)) {
          dates.push(formatDisplayDate(date));
        }
      });
      return dates;
    }
    return [];
  }, [isRecurred, plannedDepartureDateTime, recurrenceNumDays]);

  const onSubmit = values => {
    setLoading(true);
    let tripStops = stops
      .filter(x => x.selected)
      .map(stop => {
        return {
          isConnectionPoint: values[`${stop.id}.isConnectionPoint`],
          plannedArrivalDateTime: formatApiDateTime(values[`${stop.id}.plannedArrivalDateTime`]),
          plannedDepartureDateTime: formatApiDateTime(values[`${stop.id}.plannedDepartureDateTime`]),
          routeStopId: stop.id,
          stationId: stop.stationId,
        } as TripStopModel;
      });
    apiSdk.TripService.create({
      body: {
        ...values,
        plannedArrivalDateTime: formatApiDateTime(values.plannedArrivalDateTime),
        plannedDepartureDateTime: formatApiDateTime(values.plannedDepartureDateTime),
        tripStops: tripStops,
      },
    })
      .then(response => {
        if (response?.success) {
          zTb.current?.reload();
          onClose(true);
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Modal
      style={{ top: 10 }}
      open={true}
      maskClosable={false}
      width={ModalWidth.extraLarge}
      className="nta-custom-form create-edit-trip-modal"
      title={t('tripsPage_CreateTrip')}
      forceRender={true}
      okText={t('save')}
      cancelText={t('cancel')}
      okButtonProps={{ icon: <SaveOutlined />, className: 'save-btn', loading: loading }}
      onOk={() => {
        form.validateFields().then(onSubmit);
      }}
      onCancel={() => {
        onClose(false);
      }}
    >
      <Spin spinning={loading}>
        <Form form={form} layout="vertical" initialValues={{ isActive: true }}>
          <Row gutter={ROW_GUTTER}>
            <Col md={8}>
              <Card className="mtopo-card" size="small" title={t('tripsPage_TripDetail')}>
                <Form.Item label={t('tripsPage_TripName')} name="tripName" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                  <Input />
                </Form.Item>
                <Form.Item label={t('tripsPage_SeatingModel')} name="seatingModelId" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                  <SeatingModelInput />
                </Form.Item>
                <Form.Item label={t('tripsPage_Route')} name="routeId" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                  <RouteInput />
                </Form.Item>
                <Form.Item label={t('tripsPage_Group')} name="tripGroupId" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                  <TripGroupInput />
                </Form.Item>
                <Form.Item name="isActive" colon={false} valuePropName="checked">
                  <Checkbox>{t('base.IsActive')}</Checkbox>
                </Form.Item>
                <Form.Item name="isRecurred" colon={false} valuePropName="checked">
                  <Checkbox>{t('tripsPage_WantToRecurTrip')}</Checkbox>
                </Form.Item>
                {isRecurred ? (
                  <>
                    <div dangerouslySetInnerHTML={{ __html: t('tripsPage_RecurTripNote') }} />
                    <b>{t('tripsPage_TripRecurrenceDays')}</b>
                    <Form.Item name="recurrenceNumDays">
                      <Checkbox.Group options={DayOfWeekType.__getSelectOptions()} />
                    </Form.Item>
                    <Divider />
                    {recurrenceDates.map(date => {
                      return (
                        <Tag key={date} color="blue" style={{ marginBottom: 2, marginRight: 4 }}>
                          {date}
                        </Tag>
                      );
                    })}
                  </>
                ) : null}
              </Card>
            </Col>
            <Col md={16}>
              <Card className="mtopo-card" size="small" title={t('tripsPage_RouteTripDetail')} bodyStyle={{ backgroundColor: 'white' }}>
                {routeId ? (
                  <>
                    <Row gutter={ROW_GUTTER} className="components-row-header">
                      <Col md={7}>{t('tripsPage_Source')}</Col>
                      <Col md={6}>{t('tripsPage_ArrivalDateTime')}</Col>
                      <Col md={6}>{t('tripsPage_DepartureDateTime')}</Col>
                      <Col md={5}>{t('tripsPage_ConnectionPoint')}</Col>
                    </Row>
                    <Row gutter={ROW_GUTTER} className="components-row-item">
                      <Col md={13}>
                        <b>{routeDetail?.sourceStationName}</b>
                      </Col>

                      <Col md={6}>
                        <Form.Item name="plannedDepartureDateTime" rules={[{ required: true, message: '' }]}>
                          <AppDateTimePicker className="w-100" />
                        </Form.Item>
                      </Col>
                      <Col md={5} style={{ textAlign: 'center' }}></Col>
                    </Row>

                    {stops.map(stop => {
                      return (
                        <Row gutter={ROW_GUTTER} className="components-row-item" style={{ lineHeight: '47px' }}>
                          <Col md={7}>
                            <Checkbox
                              checked={stop.selected}
                              onChange={e => {
                                let _stop = stops.find(x => x.id === stop.id);
                                if (_stop) {
                                  _stop.selected = e.target.checked;
                                  setStops([...stops]);
                                }
                              }}
                            >
                              <b>{stop.stationName}</b>
                            </Checkbox>
                          </Col>
                          {stop.selected ? (
                            <>
                              <Col md={6}>
                                <Form.Item name={`${stop.id}.plannedArrivalDateTime`} rules={[{ required: true, message: '' }]}>
                                  <AppDateTimePicker className="w-100" />
                                </Form.Item>
                              </Col>
                              <Col md={6}>
                                <Form.Item name={`${stop.id}.plannedDepartureDateTime`} rules={[{ required: true, message: '' }]}>
                                  <AppDateTimePicker className="w-100" />
                                </Form.Item>
                              </Col>
                              <Col md={5} style={{ textAlign: 'center' }}>
                                <Form.Item name={`${stop.id}.isConnectionPoint`} valuePropName="checked">
                                  <Checkbox />
                                </Form.Item>
                              </Col>
                            </>
                          ) : null}
                        </Row>
                      );
                    })}
                    <Row gutter={ROW_GUTTER} className="components-row-item">
                      <Col md={7}>
                        <b>{routeDetail?.destinationStationName}</b>
                      </Col>
                      <Col md={6}>
                        <Form.Item name="plannedArrivalDateTime" rules={[{ required: true, message: '' }]}>
                          <AppDateTimePicker className="w-100" />
                        </Form.Item>
                      </Col>
                      <Col md={6}></Col>
                      <Col md={5} style={{ textAlign: 'center' }}></Col>
                    </Row>
                  </>
                ) : null}
              </Card>
            </Col>
          </Row>
        </Form>
      </Spin>
    </Modal>
  );
}
